import {isBookingRules, type BookingRules} from './booking-rules'
import type {PublicScheduleInstructor, PublicScheduleLocation} from './public-schedule.service'
import type {PassAccessSummary} from '@fitro/customer-ui/PassAccess'
import {publicFetch, resolvePublicApiBaseUrl} from '@fitro/customer-host/api'

export type PublicPassPromotion = {
    name?: string | null
    discountAmountMinor: number
    finalAmountMinor: number
    /** Set only when a price reduction may be shown: the Omnibus reference to strike through. */
    lowestPrice30dMinor?: number | null
    /** Who a new-customer price is for, e.g. `FIRST_PURCHASE` or `FIRST_CLASS_PURCHASE`; null for everyone. */
    eligibility?: string | null
}

export type PublicPassOffer = {
    accessSummary?: PassAccessSummary | null
    acceptedPaymentMethods: string[]
    colorKey?: string | null
    currency: string
    entriesTotal?: number | null
    id: string
    isBenefit: boolean
    benefitProvider?: string | null
    name: string
    priceMinor: number
    type?: string | null
    validityDays?: number | null
    automaticPromotion?: PublicPassPromotion | null
    newCustomerPromotion?: PublicPassPromotion | null
}

export type PublicPrivateBookingOffer = {
    bookingRules?: BookingRules | null
    instructors?: PublicScheduleInstructor[]
    locations?: PublicScheduleLocation[]
    id?: string
    allowedPassTemplates?: Array<{id: string; name: string; entriesTotal?: number | null; colorKey?: string | null}>
    currency: string
    description?: string | null
    durationMinutes: number
    name: string
    priceMinor: number
}

/** A recurring class on the price list; `entryMode` null is a legacy class accessed through its accepted pass products. */
export type PublicPricingClass = {
    bookingRules?: BookingRules | null
    instructors?: PublicScheduleInstructor[]
    locations?: PublicScheduleLocation[]
    fullDescriptionHtml?: string | null
    id: string
    name: string
    description?: string | null
    colorKey?: string | null
    entryMode?: 'paid' | 'free' | 'passes' | null
    /** Set for a paid class whose single entry can be bought. */
    priceMinor?: number | null
    currency?: string | null
    automaticPromotion?: PublicPassPromotion | null
    /** A better price for a first purchase or a first class. */
    newCustomerPromotion?: PublicPassPromotion | null
    benefitCardPrices: {cardName: string; priceMinor: number; cards: {provider: string; variant: string | null}[]}[]
    /** Public passes the class accepts by default. */
    acceptedPassIds: string[]
}

export type PublicAccessPass = {id: string; name: string; type: string | null; entriesTotal: number | null; isBenefit: boolean; colorKey: string | null}

export type PublicPassesPage = {
    accessTemplates?: PublicAccessPass[]
    content: PublicPassOffer[]
    privateBookingOffers: PublicPrivateBookingOffer[]
    classes: PublicPricingClass[]
}

const EMPTY_PUBLIC_PASSES_PAGE: PublicPassesPage = {content: [], privateBookingOffers: [], classes: []}
const PUBLIC_ENTRY_MODES = new Set(['paid', 'free', 'passes'])
const PUBLIC_PASS_TYPES = new Set(['open', 'entries', 'one_time'])
const PUBLIC_PAYMENT_METHODS = new Set(['online', 'cash'])

function isOptionalString(value: unknown): value is string | null | undefined {
    return value === undefined || value === null || typeof value === 'string'
}

function isOptionalPassType(value: unknown): value is string | null | undefined {
    return value === undefined || value === null || (typeof value === 'string' && PUBLIC_PASS_TYPES.has(value))
}

function isOptionalNonNegativeInteger(value: unknown): value is number | null | undefined {
    return value === undefined || value === null || (typeof value === 'number' && Number.isInteger(value) && value >= 0)
}

function isPublicPassPromotion(value: unknown): value is PublicPassPromotion {
    if (!value || typeof value !== 'object') return false
    const candidate = value as Partial<PublicPassPromotion>
    return (
        typeof candidate.discountAmountMinor === 'number'
        && Number.isInteger(candidate.discountAmountMinor)
        && candidate.discountAmountMinor > 0
        && typeof candidate.finalAmountMinor === 'number'
        && Number.isInteger(candidate.finalAmountMinor)
        && candidate.finalAmountMinor >= 0
        && isOptionalNonNegativeInteger(candidate.lowestPrice30dMinor)
        && isOptionalString(candidate.name)
        && isOptionalString(candidate.eligibility)
    )
}

export function isOptionalPublicPassPromotion(value: unknown): value is PublicPassPromotion | null | undefined {
    return value === undefined || value === null || isPublicPassPromotion(value)
}

function isPublicPaymentMethod(value: unknown): value is string {
    return typeof value === 'string' && PUBLIC_PAYMENT_METHODS.has(value)
}

function isOptionalAccessSummary(value: unknown): value is PassAccessSummary | null | undefined {
    if (value === null || value === undefined) return true
    if (typeof value !== 'object') return false
    const v = value as Partial<PassAccessSummary>
    return ['all', 'selected', 'none'].includes(v.groupMode ?? '')
        && Array.isArray(v.groupClasses) && v.groupClasses.every(n => n !== null && typeof n === 'object' && typeof n.name === 'string' && (n.colorKey === null || typeof n.colorKey === 'string'))
        && Array.isArray(v.individualClasses) && v.individualClasses.every(n => n !== null && typeof n === 'object' && typeof n.name === 'string' && (n.colorKey === null || typeof n.colorKey === 'string'))
}

function isPublicPassOffer(value: unknown): value is PublicPassOffer {
    if (!value || typeof value !== 'object') return false
    const candidate = value as {
        accessSummary?: unknown
        acceptedPaymentMethods?: unknown
        colorKey?: unknown
        currency?: unknown
        entriesTotal?: unknown
        id?: unknown
        isBenefit?: unknown
        benefitProvider?: unknown
        name?: unknown
        priceMinor?: unknown
        type?: unknown
        validityDays?: unknown
        automaticPromotion?: unknown
        newCustomerPromotion?: unknown
    }
    return (
        isOptionalAccessSummary(candidate.accessSummary)
        && typeof candidate.id === 'string'
        && candidate.id.trim().length > 0
        && typeof candidate.name === 'string'
        && candidate.name.trim().length > 0
        && typeof candidate.priceMinor === 'number'
        && Number.isInteger(candidate.priceMinor)
        && candidate.priceMinor >= 0
        && typeof candidate.currency === 'string'
        && candidate.currency.trim().length > 0
        && typeof candidate.isBenefit === 'boolean'
        && Array.isArray(candidate.acceptedPaymentMethods)
        && candidate.acceptedPaymentMethods.every(isPublicPaymentMethod)
        && isOptionalPassType(candidate.type)
        && isOptionalNonNegativeInteger(candidate.validityDays)
        && isOptionalNonNegativeInteger(candidate.entriesTotal)
        && isOptionalString(candidate.colorKey)
        && isOptionalString(candidate.benefitProvider)
        && isOptionalPublicPassPromotion(candidate.automaticPromotion)
        && isOptionalPublicPassPromotion(candidate.newCustomerPromotion)
    )
}

function isPublicAcceptedPass(value: unknown): boolean {
    if (!value || typeof value !== 'object') return false
    const pass = value as {id?: unknown; name?: unknown; entriesTotal?: unknown; colorKey?: unknown}
    return typeof pass.id === 'string' && pass.id.trim().length > 0
        && typeof pass.name === 'string' && pass.name.trim().length > 0
        && isOptionalNonNegativeInteger(pass.entriesTotal)
        && isOptionalString(pass.colorKey)
}

function isPublicAccessPass(value: unknown): boolean {
    if (!isPublicAcceptedPass(value)) return false
    const pass = value as PublicAccessPass
    return (pass.type === null || typeof pass.type === 'string') && typeof pass.isBenefit === 'boolean'
        && (pass.colorKey === null || typeof pass.colorKey === 'string')
        && (pass.entriesTotal === null || isOptionalNonNegativeInteger(pass.entriesTotal) && pass.entriesTotal !== undefined)
}

function isPublicPricingResources(value: {instructors?: unknown; locations?: unknown}): boolean {
    return (value.instructors === undefined || (Array.isArray(value.instructors) && value.instructors.every(item => {
        if (!item || typeof item !== 'object') return false
        const instructor = item as PublicScheduleInstructor
        return typeof instructor.id === 'string' && instructor.id.trim().length > 0
            && isOptionalString(instructor.firstName) && isOptionalString(instructor.lastName)
            && isOptionalString(instructor.avatarUrl)
    }))) && (value.locations === undefined || (Array.isArray(value.locations) && value.locations.every(item => {
        if (!item || typeof item !== 'object') return false
        const location = item as PublicScheduleLocation
        return typeof location.id === 'string' && location.id.trim().length > 0
            && typeof location.name === 'string' && location.name.trim().length > 0
    })))
}

function isPublicPrivateBookingOffer(value: unknown): value is PublicPrivateBookingOffer {
    if (!value || typeof value !== 'object') return false
    const candidate = value as {
        bookingRules?: unknown
        id?: unknown
        instructors?: unknown
        locations?: unknown
        allowedPassTemplates?: unknown
        currency?: unknown
        description?: unknown
        durationMinutes?: unknown
        name?: unknown
        priceMinor?: unknown
    }
    return (
        isPublicPricingResources(candidate) && isBookingRules(candidate.bookingRules) &&
        (candidate.id === undefined || typeof candidate.id === 'string') &&
        (candidate.allowedPassTemplates === undefined || (Array.isArray(candidate.allowedPassTemplates) && candidate.allowedPassTemplates.every(isPublicAcceptedPass))) &&
        typeof candidate.name === 'string'
        && candidate.name.trim().length > 0
        && typeof candidate.durationMinutes === 'number'
        && Number.isInteger(candidate.durationMinutes)
        && candidate.durationMinutes > 0
        && typeof candidate.priceMinor === 'number'
        && Number.isInteger(candidate.priceMinor)
        && candidate.priceMinor >= 0
        && typeof candidate.currency === 'string'
        && candidate.currency.trim().length > 0
        && isOptionalString(candidate.description)
    )
}

function isPublicBenefitCardPrice(value: unknown): value is PublicPricingClass['benefitCardPrices'][number] {
    if (!value || typeof value !== 'object') return false
    const candidate = value as {cardName?: unknown; priceMinor?: unknown; cards?: unknown}
    return typeof candidate.cardName === 'string' && typeof candidate.priceMinor === 'number' && isOptionalNonNegativeInteger(candidate.priceMinor)
        && Array.isArray(candidate.cards) && candidate.cards.every(isPublicBenefitCard)
}

function isPublicBenefitCard(value: unknown): boolean {
    const card = value as {provider?: unknown; variant?: unknown} | null
    return typeof card?.provider === 'string' && (card.variant === null || typeof card.variant === 'string')
}

function isPublicPricingClass(value: unknown): value is PublicPricingClass {
    if (!value || typeof value !== 'object') return false
    const candidate = value as Record<keyof PublicPricingClass, unknown>
    return (
        isPublicPricingResources(candidate) && isBookingRules(candidate.bookingRules)
        && typeof candidate.id === 'string'
        && typeof candidate.name === 'string'
        && candidate.name.trim().length > 0
        && isOptionalString(candidate.description)
        && isOptionalString(candidate.fullDescriptionHtml)
        && isOptionalString(candidate.colorKey)
        && (candidate.entryMode === undefined || candidate.entryMode === null || (typeof candidate.entryMode === 'string' && PUBLIC_ENTRY_MODES.has(candidate.entryMode)))
        && isOptionalNonNegativeInteger(candidate.priceMinor)
        && isOptionalString(candidate.currency)
        && isOptionalPublicPassPromotion(candidate.automaticPromotion)
        && isOptionalPublicPassPromotion(candidate.newCustomerPromotion)
        && Array.isArray(candidate.benefitCardPrices)
        && candidate.benefitCardPrices.every(isPublicBenefitCardPrice)
        && Array.isArray(candidate.acceptedPassIds)
        && candidate.acceptedPassIds.every((id) => typeof id === 'string')
    )
}

function parsePublicPassesPage(value: unknown): PublicPassesPage | null {
    if (!value || typeof value !== 'object') return null
    const accessTemplates = (value as {accessTemplates?: unknown}).accessTemplates
    if (accessTemplates !== undefined && (!Array.isArray(accessTemplates) || !accessTemplates.every(isPublicAccessPass))) return null
    const content = (value as {content?: unknown}).content
    const privateBookingOffers = (value as {privateBookingOffers?: unknown}).privateBookingOffers
    const classes = (value as {classes?: unknown}).classes
    if (!Array.isArray(content) || !content.every(isPublicPassOffer)) return null
    if (classes !== undefined && (!Array.isArray(classes) || !classes.every(isPublicPricingClass))) return null
    if (
        privateBookingOffers !== undefined
        && (!Array.isArray(privateBookingOffers) || !privateBookingOffers.every(isPublicPrivateBookingOffer))
    ) {
        return null
    }
    return {
        content,
        ...(accessTemplates === undefined ? {} : {accessTemplates}),
        privateBookingOffers: privateBookingOffers ?? [],
        classes: classes ?? [],
    }
}

export async function getPublicPartnerPassesStrict(slug: string): Promise<PublicPassesPage> {
    const normalizedSlug = slug.trim()
    const baseUrl = resolvePublicApiBaseUrl()
    if (!normalizedSlug || !baseUrl) throw new Error('Public passes configuration unavailable')
    const response = await publicFetch(`${baseUrl}/api/public/partners/${encodeURIComponent(normalizedSlug)}/passes`, {
        method: 'GET', credentials: 'omit', cache: 'no-store',
    })
    if (!response.ok) throw new Error(`Public passes request failed: ${response.status}`)
    const result = parsePublicPassesPage(await response.json())
    if (!result) throw new Error('Invalid public passes response')
    return result
}

export async function getPublicPartnerPasses(slug: string): Promise<PublicPassesPage> {
    try {
        return await getPublicPartnerPassesStrict(slug)
    } catch {
        return EMPTY_PUBLIC_PASSES_PAGE
    }
}
